import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { Kunde, Ware } from '../types';
import { useAuth } from '../contexts/AuthContext';
import kundeService from '../services/kundeService';
import wareService from '../services/wareService';
import berichtService from '../services/berichtService';
import mitarbeiterService, { MitarbeiterListItem } from '../services/mitarbeiterService';
import { Icon, PageHeader, LoadingScreen, ErfolgsZeichen, fehlerText } from '../components/ui';
import { ZurueckLink } from '../components/ZurueckLink';
import { markiereNeu } from '../utils/ankunft';
import { DatumFeld, UhrzeitFeld } from '../components/DatumZeit';

const EINHEITEN = ['L', 'm', 'kg', 'Stk', 'm²', 'm³'];
const DRAFT_KEY = 'bericht_entwurf';
const BESTAETIGUNG_MS = 1100;

const now = () => {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const today = () => new Date().toISOString().split('T')[0];

const calcMinutes = (start: string, end: string): number => {
  if (!start || !end) return 0;
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  const diff = (eh * 60 + em) - (sh * 60 + sm);
  return diff > 0 ? diff : 0;
};

interface FreitextArtikel {
  key: number;
  artikelbezeichnung: string;
  menge: number;
  einheit: string;
}

interface MitarbeiterEntry {
  key: number;
  userId?: number;
  name: string;
  useCustomTimes: boolean;
  abfahrtZeit: string;
  ankunftZeit: string;
  fahrzeitMinuten: number;
  arbeitszeitStart: string;
  arbeitszeitEnde: string;
  pauseMinuten: number;
  gefahreneKm: number;
}

let nextFreitextKey = 1;
let nextMaKey = 1;

/** Crop a signature canvas to just the drawn area (removes whitespace) */
const cropSignatureCanvas = (canvas: HTMLCanvasElement): string | null => {
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  const { width, height } = canvas;
  const imageData = ctx.getImageData(0, 0, width, height);
  const data = imageData.data;

  let minX = width, minY = height, maxX = 0, maxY = 0;
  let hasContent = false;

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const idx = (y * width + x) * 4;
      const r = data[idx], g = data[idx + 1], b = data[idx + 2];
      // Not white/near-white → part of the signature (ignore orientation line #e5e7eb ≈ 229,231,235)
      if (r < 200 || g < 200 || b < 200) {
        hasContent = true;
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }

  if (!hasContent) return null;

  const pad = 20;
  minX = Math.max(0, minX - pad);
  minY = Math.max(0, minY - pad);
  maxX = Math.min(width - 1, maxX + pad);
  maxY = Math.min(height - 1, maxY + pad);

  const cropW = maxX - minX + 1;
  const cropH = maxY - minY + 1;

  const tempCanvas = document.createElement('canvas');
  tempCanvas.width = cropW;
  tempCanvas.height = cropH;
  const tempCtx = tempCanvas.getContext('2d')!;
  tempCtx.fillStyle = '#fff';
  tempCtx.fillRect(0, 0, cropW, cropH);
  tempCtx.drawImage(canvas, minX, minY, cropW, cropH, 0, 0, cropW, cropH);

  return tempCanvas.toDataURL('image/png');
};


/** Dezimal-Eingabefeld das auf Mobilgeräten auch Komma als Trennzeichen akzeptiert */
const MengeInput: React.FC<{
  value: number;
  onChange: (v: number) => void;
  className?: string;
  placeholder?: string;
}> = ({ value, onChange, className, placeholder }) => {
  const [text, setText] = React.useState(String(value));

  // Externe Änderungen (z.B. per + Button) ins Textfeld übernehmen
  React.useEffect(() => {
    setText(v => {
      const parsed = parseFloat(v.replace(',', '.'));
      return parsed === value ? v : String(value);
    });
  }, [value]);

  return (
    <input
      type="text"
      inputMode="decimal"
      value={text}
      placeholder={placeholder}
      onChange={(e) => {
        const raw = e.target.value;
        setText(raw);
        const parsed = parseFloat(raw.replace(',', '.'));
        if (!isNaN(parsed)) onChange(parsed);
      }}
      onBlur={() => {
        const parsed = parseFloat(text.replace(',', '.'));
        if (!isNaN(parsed)) {
          onChange(parsed);
          setText(String(parsed));
        } else {
          setText(String(value));
        }
      }}
      className={className}
    />
  );
};

const BerichtForm: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);
  const preselectedKundeId = searchParams.get('kundeId');

  const [kunden, setKunden] = useState<Kunde[]>([]);
  const [waren, setWaren] = useState<Ware[]>([]);
  const [systemMitarbeiter, setSystemMitarbeiter] = useState<MitarbeiterListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  // Nach dem Speichern: kurze Bestätigung mit gezeichnetem Haken, dann zurück zur Liste.
  const [gespeichert, setGespeichert] = useState<{ text: string; schluessel: string } | null>(null);

  useEffect(() => {
    if (!gespeichert) return;
    const timer = window.setTimeout(() => {
      markiereNeu(gespeichert.schluessel);
      navigate('/berichte');
    }, BESTAETIGUNG_MS);
    return () => window.clearTimeout(timer);
  }, [gespeichert, navigate]);
  const [existingSignature, setExistingSignature] = useState<string | null>(null);
  const [kundeSearch, setKundeSearch] = useState('');
  const [kundeDropdownOpen, setKundeDropdownOpen] = useState(false);
  const kundeDropdownRef = useRef<HTMLDivElement>(null);

  const [form, setForm] = useState({
    kundeId: preselectedKundeId ? Number(preselectedKundeId) : 0,
    datum: today(),
    ausgefuehrteArbeiten: '',
    abfahrtZeit: '',
    ankunftZeit: '',
    arbeitszeitEnde: '',
    pauseMinuten: 0,
    gefahreneKm: 0,
    hinweise: '',
    unterschriftName: '',
    kundeNichtVorOrt: false,
  });

  // Material selection: wareId -> menge (for articles from Waren)
  const [selectedMaterials, setSelectedMaterials] = useState<Record<number, number>>({});

  // Artikel search dropdown
  const [artikelSearch, setArtikelSearch] = useState('');
  const [artikelDropdownOpen, setArtikelDropdownOpen] = useState(false);
  const artikelDropdownRef = useRef<HTMLDivElement>(null);

  // Freitext articles (not in Waren table)
  const [freitextArtikel, setFreitextArtikel] = useState<FreitextArtikel[]>([]);

  // Mitarbeiter entries
  const [mitarbeiterEntries, setMitarbeiterEntries] = useState<MitarbeiterEntry[]>([]);

  // Mitarbeiter dropdown
  const [maDropdownOpen, setMaDropdownOpen] = useState(false);
  const [maSearch, setMaSearch] = useState('');
  const maDropdownRef = useRef<HTMLDivElement>(null);

  // Signature canvas (desktop inline)
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSigned, setHasSigned] = useState(false);

  // Signature modal (mobile fullscreen)
  const modalCanvasRef = useRef<HTMLCanvasElement>(null);
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [signaturePreview, setSignaturePreview] = useState<string | null>(null);
  const [isModalDrawing, setIsModalDrawing] = useState(false);

  // Draft restore
  const [draftBanner, setDraftBanner] = useState(false);
  const draftRef = useRef<any>(null);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (kundeDropdownRef.current && !kundeDropdownRef.current.contains(e.target as Node)) {
        setKundeDropdownOpen(false);
      }
      if (maDropdownRef.current && !maDropdownRef.current.contains(e.target as Node)) {
        setMaDropdownOpen(false);
      }
      if (artikelDropdownRef.current && !artikelDropdownRef.current.contains(e.target as Node)) {
        setArtikelDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Auto-save draft to localStorage (only in create mode, not while loading or showing restore banner)
  useEffect(() => {
    if (isEditMode || loading || draftBanner || gespeichert) return;
    try {
      const draft = {
        savedAt: new Date().toISOString(),
        form,
        selectedMaterials,
        freitextArtikel,
        mitarbeiterEntries: mitarbeiterEntries.map(({ key, ...rest }) => rest),
        signaturePreview,
        hasSigned,
      };
      localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
    } catch { /* QuotaExceededError o.ä. ignorieren */ }
  }, [form, selectedMaterials, freitextArtikel, mitarbeiterEntries, signaturePreview, hasSigned, isEditMode, loading, draftBanner, gespeichert]);

  // Filtered customers for dropdown
  const filteredKunden = kunden.filter(k => {
    if (!kundeSearch) return true;
    const term = kundeSearch.toLowerCase();
    const name = [k.vorname, k.nachnameFirma].filter(Boolean).join(' ').toLowerCase();
    const ort = (k.ort || '').toLowerCase();
    return name.includes(term) || ort.includes(term);
  });

  // Filter system users not already added
  const addedUserIds = new Set(mitarbeiterEntries.filter(m => m.userId).map(m => m.userId));
  const availableMitarbeiter = systemMitarbeiter.filter(m => {
    if (addedUserIds.has(m.id)) return false;
    if (!maSearch) return true;
    const term = maSearch.toLowerCase();
    return `${m.firstName} ${m.lastName}`.toLowerCase().includes(term);
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        const [k, w, ma] = await Promise.all([
          kundeService.getAll(),
          wareService.getAll(),
          mitarbeiterService.getAll(),
        ]);
        setKunden(k);
        setWaren(w);
        setSystemMitarbeiter(ma);

        // If create mode with preselected Kunde, set default gefahreneKm
        if (!isEditMode && preselectedKundeId) {
          const preKunde = k.find(ku => ku.id === Number(preselectedKundeId));
          if (preKunde) {
            const update: Partial<typeof form> = { gefahreneKm: preKunde.entfernungKm || 0 };
            if (preKunde.anfahrtMinuten && preKunde.anfahrtMinuten > 0) {
              const ankunft = new Date();
              const abfahrt = new Date(ankunft.getTime() - preKunde.anfahrtMinuten * 60000);
              update.ankunftZeit = `${String(ankunft.getHours()).padStart(2, '0')}:${String(ankunft.getMinutes()).padStart(2, '0')}`;
              update.abfahrtZeit = `${String(abfahrt.getHours()).padStart(2, '0')}:${String(abfahrt.getMinutes()).padStart(2, '0')}`;
            }
            setForm(f => ({ ...f, ...update }));
          }
        }

        // If create mode, add current user as default Mitarbeiter
        if (!isEditMode && user) {
          const matchingMa = ma.find(m => m.id === user.id);
          setMitarbeiterEntries([{
            key: nextMaKey++,
            userId: user.id,
            name: matchingMa ? `${matchingMa.firstName} ${matchingMa.lastName}` : `${user.firstName} ${user.lastName}`,
            useCustomTimes: false,
            abfahrtZeit: '',
            ankunftZeit: '',
            fahrzeitMinuten: 0,
            arbeitszeitStart: '',
            arbeitszeitEnde: '',
            pauseMinuten: 0,
            gefahreneKm: 0,
          }]);
        }

        // If edit mode, load existing Bericht
        if (isEditMode && id) {
          const bericht = await berichtService.getById(Number(id));
          setForm({
            kundeId: bericht.kundeId,
            datum: bericht.datum,
            ausgefuehrteArbeiten: bericht.ausgefuehrteArbeiten,
            abfahrtZeit: bericht.abfahrtZeit,
            ankunftZeit: bericht.ankunftZeit,
            arbeitszeitEnde: bericht.arbeitszeitEnde,
            pauseMinuten: bericht.pauseMinuten,
            gefahreneKm: bericht.gefahreneKm,
            hinweise: bericht.hinweise || '',
            unterschriftName: bericht.unterschriftName || '',
            kundeNichtVorOrt: bericht.kundeNichtVorOrt || false,
          });

          // Load existing materials — separate into checkbox + freitext
          if (bericht.materialien && bericht.materialien.length > 0) {
            const matMap: Record<number, number> = {};
            const freitext: FreitextArtikel[] = [];
            bericht.materialien.forEach(m => {
              if (m.wareId) {
                matMap[m.wareId] = m.menge;
              } else {
                freitext.push({
                  key: nextFreitextKey++,
                  artikelbezeichnung: m.artikelbezeichnung,
                  menge: m.menge,
                  einheit: m.einheit,
                });
              }
            });
            setSelectedMaterials(matMap);
            setFreitextArtikel(freitext);
          }

          // Load existing mitarbeiter
          if (bericht.mitarbeiter && bericht.mitarbeiter.length > 0) {
            const entries: MitarbeiterEntry[] = bericht.mitarbeiter.map(ma => ({
              key: nextMaKey++,
              userId: ma.userId || undefined,
              name: ma.name,
              useCustomTimes: !!(
                ma.abfahrtZeit || ma.ankunftZeit || ma.arbeitszeitStart || ma.arbeitszeitEnde
              ),
              abfahrtZeit: ma.abfahrtZeit || '',
              ankunftZeit: ma.ankunftZeit || '',
              fahrzeitMinuten: ma.fahrzeitMinuten || 0,
              arbeitszeitStart: ma.arbeitszeitStart || '',
              arbeitszeitEnde: ma.arbeitszeitEnde || '',
              pauseMinuten: ma.pauseMinuten || 0,
              gefahreneKm: ma.gefahreneKm || 0,
            }));
            setMitarbeiterEntries(entries);
          }

          // Store existing signature to show as preview
          if (bericht.unterschriftBild) {
            setExistingSignature(bericht.unterschriftBild);
          }
        }
      } catch {
        toast.error(isEditMode ? 'Bericht konnte nicht geladen werden' : 'Kunden und Artikel konnten nicht geladen werden');
        if (isEditMode) navigate('/berichte');
      } finally {
        setLoading(false);
        // Check for saved draft (only in create mode)
        if (!isEditMode) {
          try {
            const saved = localStorage.getItem(DRAFT_KEY);
            if (saved) {
              draftRef.current = JSON.parse(saved);
              setDraftBanner(true);
            }
          } catch { /* ignore */ }
        }
      }
    };
    loadData();
    // Lädt einmal je Bericht. `user` und `preselectedKundeId` liefern nur Startwerte fürs Formular;
    // sie hier aufzunehmen würde bereits getippte Eingaben beim nächsten Rendern überschreiben.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEditMode, navigate]);

  // Computed values
  const fahrzeitMinuten = calcMinutes(form.abfahrtZeit, form.ankunftZeit);
  const arbeitszeitStart = form.ankunftZeit; // Arrival = start of work

  const setNow = (field: 'abfahrtZeit' | 'ankunftZeit' | 'arbeitszeitEnde') => {
    setForm(f => ({ ...f, [field]: now() }));
  };

  // Signature handling
  const getCanvasCoords = (e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if ('touches' in e) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY,
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const startDrawing = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const { x, y } = getCanvasCoords(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
    setHasSigned(true);
    setExistingSignature(null);
  }, []);

  const draw = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    if (!isDrawing) return;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const { x, y } = getCanvasCoords(e);
    ctx.lineTo(x, y);
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
  }, [isDrawing]);

  const stopDrawing = useCallback(() => { setIsDrawing(false); }, []);

  const clearSignature = () => {
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx || !canvasRef.current) return;
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    setHasSigned(false);
    setExistingSignature(null);
    setSignaturePreview(null);
  };

  // === Mobile Fullscreen Signature Modal ===
  const openSignatureModal = () => {
    setSignatureModalOpen(true);
    document.body.style.overflow = 'hidden';
    // Initialize modal canvas after render
    setTimeout(() => {
      const canvas = modalCanvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      // Portrait: full width, height minus button bar (60px)
      const w = window.innerWidth;
      const h = window.innerHeight - 60;
      canvas.width = w * (window.devicePixelRatio || 1);
      canvas.height = h * (window.devicePixelRatio || 1);
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.scale(window.devicePixelRatio || 1, window.devicePixelRatio || 1);
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, w, h);
      // Draw a subtle horizontal orientation line
      ctx.strokeStyle = '#e5e7eb';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(30, h * 0.7);
      ctx.lineTo(w - 30, h * 0.7);
      ctx.stroke();
    }, 50);
  };

  const closeSignatureModal = (save: boolean) => {
    if (save && modalCanvasRef.current) {
      const cropped = cropSignatureCanvas(modalCanvasRef.current);
      if (cropped) {
        setSignaturePreview(cropped);
        setHasSigned(true);
        setExistingSignature(null);
      } else {
        toast.error('Bitte zuerst unterschreiben');
        return; // Don't close if empty
      }
    }
    setSignatureModalOpen(false);
    setIsModalDrawing(false);
    document.body.style.overflow = '';
  };

  const clearModalCanvas = () => {
    const canvas = modalCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    const w = canvas.width / dpr;
    const h = canvas.height / dpr;
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, w, h);
    // Redraw horizontal orientation line
    ctx.strokeStyle = '#e5e7eb';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(30, h * 0.7);
    ctx.lineTo(w - 30, h * 0.7);
    ctx.stroke();
  };

  const getModalCanvasCoords = (e: React.MouseEvent | React.TouchEvent) => {
    const canvas = modalCanvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    // No CSS rotation — coordinates map directly
    if ('touches' in e) {
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top,
      };
    }
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  };

  const startModalDrawing = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    const ctx = modalCanvasRef.current?.getContext('2d');
    if (!ctx) return;
    const { x, y } = getModalCanvasCoords(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsModalDrawing(true);
  }, []);

  const modalDraw = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    if (!isModalDrawing) return;
    const ctx = modalCanvasRef.current?.getContext('2d');
    if (!ctx) return;
    const { x, y } = getModalCanvasCoords(e);
    ctx.lineTo(x, y);
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
  }, [isModalDrawing]);

  const stopModalDrawing = useCallback(() => { setIsModalDrawing(false); }, []);

  // Artikel search: filter waren not yet selected, matching search term
  const filteredArtikel = waren.filter(w => {
    if (selectedMaterials[w.id] !== undefined) return false;
    if (!artikelSearch.trim()) return false;
    return w.artikelbezeichnung.toLowerCase().includes(artikelSearch.toLowerCase());
  });

  // Materials — add from search, remove, update menge
  const addMaterialFromSearch = (wareId: number, menge: number) => {
    setSelectedMaterials(prev => ({ ...prev, [wareId]: menge > 0 ? menge : 1 }));
  };

  const removeSelectedMaterial = (wareId: number) => {
    setSelectedMaterials(prev => {
      const next = { ...prev };
      delete next[wareId];
      return next;
    });
  };

  const setMaterialMenge = (wareId: number, menge: number) => {
    setSelectedMaterials(prev => ({ ...prev, [wareId]: menge }));
  };

  // Freitext-Artikel
  const addFreitextArtikel = () => {
    setFreitextArtikel(prev => [...prev, {
      key: nextFreitextKey++,
      artikelbezeichnung: '',
      menge: 1,
      einheit: 'Stk',
    }]);
  };

  const updateFreitextArtikel = (key: number, field: keyof FreitextArtikel, value: any) => {
    setFreitextArtikel(prev => prev.map(a => a.key === key ? { ...a, [field]: value } : a));
  };

  const removeFreitextArtikel = (key: number) => {
    setFreitextArtikel(prev => prev.filter(a => a.key !== key));
  };

  // Mitarbeiter
  const addMitarbeiterFromSystem = (user: MitarbeiterListItem) => {
    setMitarbeiterEntries(prev => [...prev, {
      key: nextMaKey++,
      userId: user.id,
      name: `${user.firstName} ${user.lastName}`,
      useCustomTimes: false,
      abfahrtZeit: '',
      ankunftZeit: '',
      fahrzeitMinuten: 0,
      arbeitszeitStart: '',
      arbeitszeitEnde: '',
      pauseMinuten: 0,
      gefahreneKm: 0,
    }]);
    setMaDropdownOpen(false);
    setMaSearch('');
  };

  const addFreitextMitarbeiter = () => {
    setMitarbeiterEntries(prev => [...prev, {
      key: nextMaKey++,
      name: '',
      useCustomTimes: false,
      abfahrtZeit: '',
      ankunftZeit: '',
      fahrzeitMinuten: 0,
      arbeitszeitStart: '',
      arbeitszeitEnde: '',
      pauseMinuten: 0,
      gefahreneKm: 0,
    }]);
  };

  const updateMitarbeiter = (key: number, field: keyof MitarbeiterEntry, value: any) => {
    setMitarbeiterEntries(prev => prev.map(m => m.key === key ? { ...m, [field]: value } : m));
  };

  const removeMitarbeiter = (key: number) => {
    setMitarbeiterEntries(prev => prev.filter(m => m.key !== key));
  };

  const toggleCustomTimes = (key: number) => {
    setMitarbeiterEntries(prev => prev.map(m => {
      if (m.key !== key) return m;
      if (!m.useCustomTimes) {
        // Switching to custom: pre-fill with global times
        return {
          ...m,
          useCustomTimes: true,
          abfahrtZeit: form.abfahrtZeit,
          ankunftZeit: form.ankunftZeit,
          arbeitszeitStart: form.ankunftZeit,
          arbeitszeitEnde: form.arbeitszeitEnde,
          fahrzeitMinuten: fahrzeitMinuten,
          pauseMinuten: form.pauseMinuten,
          gefahreneKm: form.gefahreneKm,
        };
      } else {
        return { ...m, useCustomTimes: false };
      }
    }));
  };

  // Compute MA summary
  const getMaSummary = () => {
    let totalFahrzeit = 0;
    let totalArbeitszeit = 0;
    let totalKm = 0;

    mitarbeiterEntries.forEach(ma => {
      if (ma.useCustomTimes) {
        totalFahrzeit += calcMinutes(ma.abfahrtZeit, ma.ankunftZeit);
        const az = calcMinutes(ma.arbeitszeitStart || ma.ankunftZeit, ma.arbeitszeitEnde) - ma.pauseMinuten;
        totalArbeitszeit += Math.max(0, az);
        totalKm += ma.gefahreneKm;
      } else {
        totalFahrzeit += fahrzeitMinuten;
        const az = calcMinutes(arbeitszeitStart, form.arbeitszeitEnde) - form.pauseMinuten;
        totalArbeitszeit += Math.max(0, az);
        totalKm += form.gefahreneKm;
      }
    });

    return { totalFahrzeit, totalArbeitszeit, totalKm };
  };

  const restoreDraft = () => {
    const draft = draftRef.current;
    if (!draft) return;
    setForm(draft.form);
    setSelectedMaterials(draft.selectedMaterials);
    setFreitextArtikel(draft.freitextArtikel.map((a: any) => ({ ...a, key: nextFreitextKey++ })));
    setMitarbeiterEntries(draft.mitarbeiterEntries.map((m: any) => ({ ...m, key: nextMaKey++ })));
    setSignaturePreview(draft.signaturePreview);
    setHasSigned(draft.hasSigned);
    setDraftBanner(false);
  };

  const discardDraft = () => {
    localStorage.removeItem(DRAFT_KEY);
    draftRef.current = null;
    setDraftBanner(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.kundeId) { toast.error('Bitte Kunden auswählen'); return; }
    const validMitarbeiter = mitarbeiterEntries.filter(m => m.name.trim());
    if (validMitarbeiter.length === 0) { toast.error('Mindestens ein Mitarbeiter erforderlich'); return; }

    setSubmitting(true);
    try {
      let unterschriftBild: string | undefined;
      if (hasSigned && signaturePreview) {
        unterschriftBild = signaturePreview; // Mobile fullscreen
      } else if (hasSigned && canvasRef.current) {
        unterschriftBild = canvasRef.current.toDataURL('image/png'); // Desktop inline
      } else if (existingSignature) {
        unterschriftBild = existingSignature;
      }

      // Build materialien from checkbox + freitext
      const checkboxMaterialien = Object.entries(selectedMaterials).map(([wareIdStr, menge]) => {
        const wareId = Number(wareIdStr);
        const ware = waren.find(w => w.id === wareId);
        return {
          wareId,
          menge,
          einheit: ware?.einheit || '',
          artikelbezeichnung: ware?.artikelbezeichnung || '',
        };
      });

      const freitextMaterialien = freitextArtikel
        .filter(a => a.artikelbezeichnung.trim())
        .map(a => ({
          wareId: undefined as number | undefined,
          menge: a.menge,
          einheit: a.einheit,
          artikelbezeichnung: a.artikelbezeichnung,
        }));

      // Build mitarbeiter
      const mitarbeiterPayload = mitarbeiterEntries
        .filter(m => m.name.trim())
        .map(m => {
          if (m.useCustomTimes) {
            return {
              userId: m.userId,
              name: m.name,
              abfahrtZeit: m.abfahrtZeit || undefined,
              ankunftZeit: m.ankunftZeit || undefined,
              fahrzeitMinuten: calcMinutes(m.abfahrtZeit, m.ankunftZeit),
              arbeitszeitStart: m.arbeitszeitStart || m.ankunftZeit || undefined,
              arbeitszeitEnde: m.arbeitszeitEnde || undefined,
              pauseMinuten: m.pauseMinuten,
              gefahreneKm: m.gefahreneKm,
            };
          } else {
            return {
              userId: m.userId,
              name: m.name,
              abfahrtZeit: form.abfahrtZeit || undefined,
              ankunftZeit: form.ankunftZeit || undefined,
              fahrzeitMinuten,
              arbeitszeitStart: arbeitszeitStart || undefined,
              arbeitszeitEnde: form.arbeitszeitEnde || undefined,
              pauseMinuten: form.pauseMinuten,
              gefahreneKm: form.gefahreneKm,
            };
          }
        });

      const payload = {
        kundeId: form.kundeId,
        datum: form.datum,
        ausgefuehrteArbeiten: form.ausgefuehrteArbeiten,
        abfahrtZeit: form.abfahrtZeit,
        ankunftZeit: form.ankunftZeit,
        fahrzeitMinuten,
        arbeitszeitStart,
        arbeitszeitEnde: form.arbeitszeitEnde,
        pauseMinuten: form.pauseMinuten,
        gefahreneKm: form.gefahreneKm,
        hinweise: form.hinweise || undefined,
        unterschriftBild,
        unterschriftName: form.unterschriftName || undefined,
        kundeNichtVorOrt: form.kundeNichtVorOrt,
        materialien: [...checkboxMaterialien, ...freitextMaterialien],
        mitarbeiter: mitarbeiterPayload,
      };

      if (isEditMode && id) {
        await berichtService.update(Number(id), payload);
        setGespeichert({ text: 'Bericht gespeichert', schluessel: `bericht-${id}` });
      } else {
        const neu = await berichtService.create(payload);
        localStorage.removeItem(DRAFT_KEY);
        setGespeichert({ text: 'Bericht angelegt', schluessel: `bericht-${neu.id}` });
      }
    } catch (err: any) {
      toast.error(fehlerText(err, 'Bericht konnte nicht gespeichert werden'));
      setSubmitting(false);
    }
  };

  if (loading) {
    return <LoadingScreen />;
  }

  const selectedKunde = kunden.find(k => k.id === form.kundeId);
  const maSummary = mitarbeiterEntries.length > 0 ? getMaSummary() : null;

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-3">
        <ZurueckLink fallback="/berichte" fallbackLabel="Zurück zu den Berichten" />
      </div>
      <PageHeader title={isEditMode ? 'Bericht bearbeiten' : 'Neuer Arbeitsbericht'} />

      {gespeichert && (
        <div className="ui-fade fixed inset-0 z-50 flex items-center justify-center bg-white/85 backdrop-blur-sm" role="status">
          <div className="ui-pop flex flex-col items-center gap-3 text-brand-600">
            <ErfolgsZeichen className="h-24 w-24" />
            <p className="text-lg font-semibold text-gray-900">{gespeichert.text}</p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Draft restore banner */}
        {draftBanner && draftRef.current && (
          <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <Icon.Warnung className="w-5 h-5 text-amber-500 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <span className="text-sm font-medium text-amber-800">Nicht gespeicherter Entwurf gefunden</span>
              <span className="text-xs text-amber-600 block">
                {new Date(draftRef.current.savedAt).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
            <button type="button" onClick={restoreDraft}
              className="flex-shrink-0 text-sm font-medium text-amber-700 hover:text-amber-900 px-2 py-1 rounded hover:bg-amber-100 transition-colors">
              Wiederherstellen
            </button>
            <button type="button" onClick={discardDraft}
              className="flex-shrink-0 text-sm text-amber-500 hover:text-amber-700 px-2 py-1 rounded hover:bg-amber-100 transition-colors">
              Verwerfen
            </button>
          </div>
        )}

        {/* Kunde & Datum */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Kunde & Datum</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div ref={kundeDropdownRef}>
              <label className="block text-sm font-medium mb-1">Kunde *</label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Kunde suchen"
                  value={kundeDropdownOpen ? kundeSearch : (
                    selectedKunde
                      ? [selectedKunde.vorname, selectedKunde.nachnameFirma].filter(Boolean).join(' ') + (selectedKunde.ort ? ` (${selectedKunde.ort})` : '')
                      : ''
                  )}
                  onChange={(e) => {
                    setKundeSearch(e.target.value);
                    if (!kundeDropdownOpen) setKundeDropdownOpen(true);
                  }}
                  onFocus={() => {
                    setKundeDropdownOpen(true);
                    setKundeSearch('');
                  }}
                  className="w-full p-2 border border-gray-300 rounded-lg pr-8"
                />
                {form.kundeId > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      setForm({ ...form, kundeId: 0 });
                      setKundeSearch('');
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <Icon.Close className="w-4 h-4" />
                  </button>
                )}

                {kundeDropdownOpen && (
                  <div className="absolute z-20 left-0 right-0 top-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {filteredKunden.length === 0 ? (
                      <div className="px-4 py-3 text-sm text-gray-500">Kein Kunde gefunden</div>
                    ) : (
                      filteredKunden.map(k => {
                        const name = [k.vorname, k.nachnameFirma].filter(Boolean).join(' ');
                        const isActive = k.id === form.kundeId;
                        return (
                          <button
                            key={k.id}
                            type="button"
                            onClick={() => {
                              const update: Partial<typeof form> = {
                                kundeId: k.id,
                                gefahreneKm: k.entfernungKm || 0,
                              };
                              if (k.anfahrtMinuten && k.anfahrtMinuten > 0) {
                                const ankunft = new Date();
                                const abfahrt = new Date(ankunft.getTime() - k.anfahrtMinuten * 60000);
                                update.ankunftZeit = `${String(ankunft.getHours()).padStart(2, '0')}:${String(ankunft.getMinutes()).padStart(2, '0')}`;
                                update.abfahrtZeit = `${String(abfahrt.getHours()).padStart(2, '0')}:${String(abfahrt.getMinutes()).padStart(2, '0')}`;
                              }
                              setForm(f => ({ ...f, ...update }));
                              setKundeSearch('');
                              setKundeDropdownOpen(false);
                            }}
                            className={`w-full text-left px-4 py-2.5 text-sm hover:bg-brand-50 transition-colors ${
                              isActive ? 'bg-brand-50 text-brand-700 font-medium' : 'text-gray-700'
                            }`}
                          >
                            <div className="font-medium">{name}</div>
                            {k.ort && <div className="text-xs text-gray-400">{k.ort}</div>}
                          </button>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
              {selectedKunde && selectedKunde.strasse && !kundeDropdownOpen && (
                <p className="text-xs text-gray-500 mt-1">
                  {selectedKunde.strasse}, {selectedKunde.plz} {selectedKunde.ort}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Datum *</label>
              <DatumFeld required value={form.datum}
                onChange={(v) => setForm({ ...form, datum: v })}
                className="w-full p-2 border border-gray-300 rounded-lg" />
            </div>
          </div>
        </div>

        {/* Ausgeführte Arbeiten */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Ausgeführte Arbeiten</h2>
          <textarea required value={form.ausgefuehrteArbeiten}
            onChange={(e) => setForm({ ...form, ausgefuehrteArbeiten: e.target.value })}
            rows={4}
            placeholder="Beschreibung der durchgeführten Arbeiten..."
            className="w-full p-2 border border-gray-300 rounded-lg" />
        </div>

        {/* Zeiten */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Fahrzeit & Arbeitszeit</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium mb-1">Abfahrt</label>
              <div className="flex gap-2">
                <UhrzeitFeld required value={form.abfahrtZeit}
                  onChange={(v) => setForm({ ...form, abfahrtZeit: v })}
                  className="flex-1 p-2 border border-gray-300 rounded-lg" />
                <button type="button" onClick={() => setNow('abfahrtZeit')}
                  className="px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 text-sm"
                  title="Aktuelle Uhrzeit">
                  Jetzt
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Ankunft (= Start Arbeitszeit)</label>
              <div className="flex gap-2">
                <UhrzeitFeld required value={form.ankunftZeit}
                  onChange={(v) => setForm({ ...form, ankunftZeit: v })}
                  className="flex-1 p-2 border border-gray-300 rounded-lg" />
                <button type="button" onClick={() => setNow('ankunftZeit')}
                  className="px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 text-sm"
                  title="Aktuelle Uhrzeit">
                  Jetzt
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Fahrzeit</label>
              <div className="p-2 bg-gray-50 border border-gray-200 rounded-lg text-center font-semibold">
                {fahrzeitMinuten} Minuten
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Start Arbeitszeit</label>
              <div className="p-2 bg-gray-50 border border-gray-200 rounded-lg text-center">
                {arbeitszeitStart || '--:--'}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Ende Arbeitszeit</label>
              <div className="flex gap-2">
                <UhrzeitFeld required value={form.arbeitszeitEnde}
                  onChange={(v) => setForm({ ...form, arbeitszeitEnde: v })}
                  className="flex-1 p-2 border border-gray-300 rounded-lg" />
                <button type="button" onClick={() => setNow('arbeitszeitEnde')}
                  className="px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 text-sm"
                  title="Aktuelle Uhrzeit">
                  Jetzt
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Arbeitszeit netto</label>
              <div className="p-2 bg-gray-50 border border-gray-200 rounded-lg text-center font-semibold">
                {arbeitszeitStart && form.arbeitszeitEnde
                  ? `${calcMinutes(arbeitszeitStart, form.arbeitszeitEnde) - form.pauseMinuten} Min.`
                  : '-- Min.'}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium mb-1">Pause (Minuten)</label>
              <input type="number" min={0} value={form.pauseMinuten}
                onChange={(e) => setForm({ ...form, pauseMinuten: Number(e.target.value) })}
                className="w-full p-2 border border-gray-300 rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Gefahrene km</label>
              <MengeInput value={form.gefahreneKm}
                onChange={(v) => setForm({ ...form, gefahreneKm: v })}
                className="w-full p-2 border border-gray-300 rounded-lg" />
            </div>
          </div>
        </div>

        {/* Mitarbeiter */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">
            Mitarbeiter
            {mitarbeiterEntries.length > 0 && (
              <span className="ml-2 text-sm font-normal text-brand-600">
                ({mitarbeiterEntries.length})
              </span>
            )}
          </h2>

          {/* Added Mitarbeiter */}
          {mitarbeiterEntries.length > 0 && (
            <div className="space-y-3 mb-4">
              {mitarbeiterEntries.map(ma => (
                <div key={ma.key} className="border border-gray-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2">
                    {/* Name field: read-only for system users, editable for freitext */}
                    {ma.userId ? (
                      <span className="flex-1 text-sm font-medium text-gray-900">{ma.name}</span>
                    ) : (
                      <input
                        type="text"
                        placeholder="Name des Mitarbeiters..."
                        value={ma.name}
                        onChange={(e) => updateMitarbeiter(ma.key, 'name', e.target.value)}
                        className="flex-1 p-1.5 border border-gray-300 rounded-lg text-sm"
                      />
                    )}
                    <button
                      type="button"
                      onClick={() => toggleCustomTimes(ma.key)}
                      className={`text-xs px-2 py-1 rounded-lg border transition-colors ${
                        ma.useCustomTimes
                          ? 'bg-brand-50 border-brand-300 text-brand-700'
                          : 'bg-gray-50 border-gray-200 text-gray-500 hover:bg-gray-100'
                      }`}
                    >
                      {ma.useCustomTimes ? 'Individuelle Zeiten' : 'Globale Zeiten'}
                    </button>
                    <button
                      type="button"
                      onClick={() => removeMitarbeiter(ma.key)}
                      className="text-red-400 hover:text-red-600 p-1"
                    >
                      <Icon.Close className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Custom times panel */}
                  {ma.useCustomTimes && (
                    <div className="bg-gray-50 rounded-lg p-3 mt-2">
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">Abfahrt</label>
                          <UhrzeitFeld value={ma.abfahrtZeit}
                            onChange={(v) => updateMitarbeiter(ma.key, 'abfahrtZeit', v)}
                            className="w-full p-1.5 border border-gray-300 rounded-lg text-sm" />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">Ankunft</label>
                          <UhrzeitFeld value={ma.ankunftZeit}
                            onChange={(v) => updateMitarbeiter(ma.key, 'ankunftZeit', v)}
                            className="w-full p-1.5 border border-gray-300 rounded-lg text-sm" />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">Fahrzeit</label>
                          <div className="p-1.5 bg-white border border-gray-200 rounded-lg text-sm text-center">
                            {calcMinutes(ma.abfahrtZeit, ma.ankunftZeit)} Min.
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">Arbeitszeit Start</label>
                          <UhrzeitFeld value={ma.arbeitszeitStart}
                            onChange={(v) => updateMitarbeiter(ma.key, 'arbeitszeitStart', v)}
                            className="w-full p-1.5 border border-gray-300 rounded-lg text-sm" />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">Arbeitszeit Ende</label>
                          <UhrzeitFeld value={ma.arbeitszeitEnde}
                            onChange={(v) => updateMitarbeiter(ma.key, 'arbeitszeitEnde', v)}
                            className="w-full p-1.5 border border-gray-300 rounded-lg text-sm" />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">Pause (Min.)</label>
                          <input type="number" min={0} value={ma.pauseMinuten}
                            onChange={(e) => updateMitarbeiter(ma.key, 'pauseMinuten', Number(e.target.value))}
                            className="w-full p-1.5 border border-gray-300 rounded-lg text-sm" />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">Gefahrene km</label>
                          <MengeInput value={ma.gefahreneKm}
                            onChange={(v) => updateMitarbeiter(ma.key, 'gefahreneKm', v)}
                            className="w-full p-1.5 border border-gray-300 rounded-lg text-sm" />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Summary row */}
          {maSummary && mitarbeiterEntries.length > 0 && (
            <div className="bg-brand-50 border border-brand-200 rounded-lg p-3 mb-4">
              <div className="text-xs font-semibold text-brand-700 mb-1">Summe ({mitarbeiterEntries.length} Mitarbeiter)</div>
              <div className="flex flex-wrap gap-4 text-sm text-brand-800">
                <span>Fahrzeit: {maSummary.totalFahrzeit} Min.</span>
                <span>Arbeitszeit: {maSummary.totalArbeitszeit} Min.</span>
                <span>km: {maSummary.totalKm}</span>
              </div>
            </div>
          )}

          {/* Add buttons */}
          <div className="flex gap-2" ref={maDropdownRef}>
            <div className="relative">
              <button
                type="button"
                onClick={() => { setMaDropdownOpen(!maDropdownOpen); setMaSearch(''); }}
                className="px-3 py-2 bg-brand-50 border border-brand-200 text-brand-700 rounded-lg hover:bg-brand-100 text-sm flex items-center gap-1"
              >
                <Icon.Plus className="w-4 h-4" />
                Mitarbeiter
              </button>

              {maDropdownOpen && (
                <div className="absolute z-20 left-0 top-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg w-64">
                  <div className="p-2">
                    <input
                      type="text"
                      placeholder="Suchen..."
                      value={maSearch}
                      onChange={(e) => setMaSearch(e.target.value)}
                      className="w-full p-1.5 border border-gray-300 rounded-lg text-sm"
                      autoFocus
                    />
                  </div>
                  <div className="max-h-48 overflow-y-auto">
                    {availableMitarbeiter.length === 0 ? (
                      <div className="px-4 py-3 text-sm text-gray-500">
                        {maSearch ? 'Nicht gefunden' : 'Alle Mitarbeiter bereits hinzugefügt'}
                      </div>
                    ) : (
                      availableMitarbeiter.map(m => (
                        <button
                          key={m.id}
                          type="button"
                          onClick={() => addMitarbeiterFromSystem(m)}
                          className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-brand-50 transition-colors"
                        >
                          {m.firstName} {m.lastName}
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={addFreitextMitarbeiter}
              className="px-3 py-2 bg-gray-50 border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-100 text-sm flex items-center gap-1"
            >
              <Icon.Plus className="w-4 h-4" />
              Freitext
            </button>
          </div>
        </div>

        {/* Material — Suche + Freitext */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">
            Verwendetes Material
            {(Object.keys(selectedMaterials).length + freitextArtikel.length) > 0 && (
              <span className="ml-2 text-sm font-normal text-brand-600">
                ({Object.keys(selectedMaterials).length + freitextArtikel.length})
              </span>
            )}
          </h2>

          {/* Artikel search */}
          <div ref={artikelDropdownRef} className="relative mb-4">
            <div className="relative">
              <Icon.Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Artikel suchen"
                value={artikelSearch}
                onChange={(e) => {
                  setArtikelSearch(e.target.value);
                  if (e.target.value.trim()) setArtikelDropdownOpen(true);
                  else setArtikelDropdownOpen(false);
                }}
                onFocus={() => {
                  if (artikelSearch.trim()) setArtikelDropdownOpen(true);
                }}
                className="w-full p-2 pl-9 border border-gray-300 rounded-lg text-sm"
              />
            </div>

            {artikelDropdownOpen && artikelSearch.trim() && (
              <div className="absolute z-20 left-0 right-0 top-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-72 overflow-y-auto">
                {filteredArtikel.length === 0 ? (
                  <div className="px-4 py-3 text-sm text-gray-500">Kein Artikel gefunden</div>
                ) : (
                  filteredArtikel.map(ware => (
                    <button
                      key={ware.id}
                      type="button"
                      onClick={() => {
                        addMaterialFromSearch(ware.id, 1);
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-gray-50 border-b border-gray-100 last:border-0 text-left"
                    >
                      <span className="flex-1 min-w-0 text-sm text-gray-900 truncate">{ware.artikelbezeichnung}</span>
                      <span className="text-xs text-brand-700 bg-brand-100 px-2 py-0.5 rounded-full flex-shrink-0">{ware.einheit}</span>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Selected articles list */}
          {Object.keys(selectedMaterials).length > 0 && (
            <div className="space-y-1 mb-4">
              {Object.entries(selectedMaterials).map(([wareIdStr, menge]) => {
                const wareId = Number(wareIdStr);
                const ware = waren.find(w => w.id === wareId);
                if (!ware) return null;
                return (
                  <div key={wareId} className="flex items-center gap-2 p-2.5 rounded-lg border border-brand-200 bg-brand-50">
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-medium text-gray-900 truncate block">{ware.artikelbezeichnung}</span>
                    </div>
                    <span className="text-xs text-brand-700 bg-brand-100 px-2 py-0.5 rounded-full flex-shrink-0">
                      {ware.einheit}
                    </span>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <MengeInput value={menge}
                        onChange={(v) => setMaterialMenge(wareId, v)}
                        className="w-12 text-center p-1 border border-gray-300 rounded-lg text-sm font-semibold"
                      />
                      <button type="button"
                        onClick={() => setMaterialMenge(wareId, menge + 1)}
                        className="w-7 h-7 flex items-center justify-center rounded-lg border border-gray-300 bg-white hover:bg-gray-100 text-gray-600 font-bold text-sm"
                      >+</button>
                    </div>
                    <button type="button" onClick={() => removeSelectedMaterial(wareId)}
                      className="text-red-400 hover:text-red-600 p-1 flex-shrink-0">
                      <Icon.Close className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Freitext articles */}
          {freitextArtikel.length > 0 && (
            <div className="space-y-2 mb-4">
              <h3 className="text-sm font-medium text-gray-700">Freitext-Artikel</h3>
              {freitextArtikel.map(art => (
                <div key={art.key} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg border border-gray-200">
                  <input
                    type="text"
                    placeholder="Artikelbezeichnung"
                    value={art.artikelbezeichnung}
                    onChange={(e) => updateFreitextArtikel(art.key, 'artikelbezeichnung', e.target.value)}
                    className="flex-1 p-1.5 border border-gray-300 rounded-lg text-sm min-w-0"
                  />
                  <MengeInput
                    value={art.menge}
                    onChange={(v) => updateFreitextArtikel(art.key, 'menge', v)}
                    className="w-20 p-1.5 border border-gray-300 rounded-lg text-sm text-center"
                    placeholder="Menge"
                  />
                  <select
                    value={art.einheit}
                    onChange={(e) => updateFreitextArtikel(art.key, 'einheit', e.target.value)}
                    className="p-1.5 border border-gray-300 rounded-lg text-sm"
                  >
                    {EINHEITEN.map(e => <option key={e} value={e}>{e}</option>)}
                  </select>
                  <button
                    type="button"
                    onClick={() => removeFreitextArtikel(art.key)}
                    className="text-red-400 hover:text-red-600 p-1 flex-shrink-0"
                  >
                    <Icon.Close className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Add Freitext button */}
          <button
            type="button"
            onClick={addFreitextArtikel}
            className="px-3 py-2 bg-gray-50 border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-100 text-sm flex items-center gap-1"
          >
            <Icon.Plus className="w-4 h-4" />
            Freitext-Artikel
          </button>
        </div>

        {/* Hinweise */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Hinweise</h2>
          <textarea value={form.hinweise}
            onChange={(e) => setForm({ ...form, hinweise: e.target.value })}
            rows={3}
            placeholder="Zusätzliche Hinweise oder Anmerkungen..."
            className="w-full p-2 border border-gray-300 rounded-lg" />
        </div>

        {/* Unterschrift */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Unterschrift</h2>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Name des Unterzeichners</label>
            <input type="text" value={form.unterschriftName}
              onChange={(e) => setForm({ ...form, unterschriftName: e.target.value })}
              placeholder="Name eingeben"
              className="w-full p-2 border border-gray-300 rounded-lg" />
          </div>

          {/* Existing signature preview (edit mode) */}
          {existingSignature && !hasSigned && (
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Vorhandene Unterschrift</label>
              <div className="border-2 border-gray-200 rounded-lg p-2 bg-white inline-block">
                <img src={existingSignature} alt="Vorhandene Unterschrift" className="max-h-24" />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Zeichne unten neu, um die Unterschrift zu ersetzen.
              </p>
            </div>
          )}

          {/* Mobile: Button to open fullscreen signature */}
          <div className="md:hidden mb-2">
            <label className="block text-sm font-medium mb-1">
              {existingSignature && !hasSigned ? 'Neue Unterschrift (optional)' : 'Unterschrift'}
            </label>
            {signaturePreview && hasSigned ? (
              <div>
                <div className="border-2 border-brand-300 rounded-lg p-2 bg-white">
                  <img src={signaturePreview} alt="Unterschrift" className="w-full max-h-32 object-contain" />
                </div>
                <div className="flex gap-3 mt-2">
                  <button type="button" onClick={openSignatureModal}
                    className="text-sm text-brand-600 hover:text-brand-800 font-medium">
                    Neu unterschreiben
                  </button>
                  <button type="button" onClick={() => { setSignaturePreview(null); setHasSigned(false); }}
                    className="text-sm text-gray-600 hover:text-gray-900">
                    Unterschrift löschen
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={openSignatureModal}
                className="w-full py-8 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 hover:bg-gray-100 hover:border-brand-400 transition-colors flex flex-col items-center gap-2"
              >
                <Icon.Unterschrift className="w-8 h-8 text-gray-400" />
                <span className="text-sm font-medium text-gray-600">Tippen zum Unterschreiben</span>
                <span className="text-xs text-gray-400">Öffnet Vollbild im Querformat</span>
              </button>
            )}
          </div>

          {/* Desktop: Inline canvas (hidden on mobile) */}
          <div className="hidden md:block mb-2">
            <label className="block text-sm font-medium mb-1">
              {existingSignature && !hasSigned ? 'Neue Unterschrift (optional)' : 'Unterschrift'}
            </label>
            <div className="border-2 border-gray-300 rounded-lg overflow-hidden bg-white"
              style={{ touchAction: 'none' }}>
              <canvas
                ref={canvasRef}
                width={600}
                height={200}
                className="w-full cursor-crosshair"
                style={{ height: '150px' }}
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
              />
            </div>
            <button type="button" onClick={clearSignature}
              className="mt-2 text-sm text-gray-600 hover:text-gray-900">
              Unterschrift löschen
            </button>
          </div>

          {/* Kunde nicht vor Ort */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.kundeNichtVorOrt}
                onChange={(e) => setForm({ ...form, kundeNichtVorOrt: e.target.checked })}
                className="w-5 h-5 text-brand-600 rounded border-gray-300 focus:ring-brand-500 cursor-pointer"
              />
              <div>
                <span className="text-sm font-medium text-gray-900">Kunde war nicht vor Ort</span>
                <p className="text-xs text-gray-500">Wird als Vermerk im Bericht dokumentiert</p>
              </div>
            </label>
          </div>
        </div>

        {/* Fullscreen Signature Modal (mobile) */}
        {signatureModalOpen && (
          <div className="fixed inset-0 z-[200] bg-white flex flex-col">
            {/* Top button bar */}
            <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
              <button
                type="button"
                onClick={() => closeSignatureModal(false)}
                className="px-4 py-2 text-gray-600 bg-white border border-gray-300 rounded-lg text-sm font-medium"
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={clearModalCanvas}
                className="px-4 py-2 text-gray-600 bg-white border border-gray-300 rounded-lg text-sm font-medium"
              >
                Löschen
              </button>
              <button
                type="button"
                onClick={() => closeSignatureModal(true)}
                className="px-6 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700"
              >
                Übernehmen
              </button>
            </div>
            {/* Canvas area — fills remaining screen, NO rotation */}
            <div className="flex-1 relative" style={{ touchAction: 'none' }}>
              <canvas
                ref={modalCanvasRef}
                className="absolute inset-0 bg-white"
                onMouseDown={startModalDrawing}
                onMouseMove={modalDraw}
                onMouseUp={stopModalDrawing}
                onMouseLeave={stopModalDrawing}
                onTouchStart={startModalDrawing}
                onTouchMove={modalDraw}
                onTouchEnd={stopModalDrawing}
              />
            </div>
          </div>
        )}

        {/* Submit */}
        <div className="flex justify-end gap-4">
          <button type="button" onClick={() => navigate(-1)}
            className="px-6 py-3 text-gray-600 bg-gray-200 rounded-lg hover:bg-gray-300">
            Abbrechen
          </button>
          <button type="submit" disabled={submitting}
            className="px-6 py-3 bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50">
            {submitting
              ? 'Wird gespeichert …'
              : isEditMode ? 'Speichern' : 'Bericht anlegen'
            }
          </button>
        </div>
      </form>
    </div>
  );
};

export default BerichtForm;
